import { requireCompletedProfile } from "@hisani/auth";
import { computeNextDue } from "../domain/vaccinations/vaccination-interval.js";
import { isKnownVaccineType } from "../domain/vaccinations/vaccine-type.js";
import { createVaccinationRecordSchema, listHorseVaccinationRecordsSchema, logVaccinationRecordOutcomeSchema, stableConflict, stableInvalidTransition, stableNotFound, } from "../validation.js";
import { beginSealedRecordDocumentUpload, getSealedRecordDocumentUrl, requireFeatureMembership, requireHorseAcceptingSealedRecords, verifyAndClaimSealedRecordDocuments, } from "./sealed-record-support.js";
const AREA = "vaccinations";
const FEATURE = "VACCINATIONS";
async function requireKnownVaccineType(dependencies, vaccineType) {
    const policy = await dependencies.policy.get();
    if (!isKnownVaccineType(policy.vaccineTypes, vaccineType)) {
        throw stableInvalidTransition("This vaccine type is not configured for this Stable.");
    }
    return policy;
}
export function beginVaccinationRecordDocumentUpload(principal, input, dependencies) {
    return beginSealedRecordDocumentUpload(principal, input, AREA, FEATURE, dependencies);
}
/**
 * Creates a scheduled placeholder, a directly logged dose, or both at once
 * (`docs/products/stable.md`: "Provide a due date, a date administered, or
 * both" — `createVaccinationRecordSchema`). `next_due` is computed from the
 * vaccine type's configured interval (`computeNextDue`) unless the caller set
 * `nextDueOverridden` — either a manual vet override or a value read
 * directly off the scanned stamp both count as an override, per
 * `docs/products/stable.md`: "overridable by the logging vet or read
 * directly off the stamp."
 */
export async function createVaccinationRecord(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = createVaccinationRecordSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    await requireHorseAcceptingSealedRecords(dependencies, parsed.stableId, parsed.horseId);
    const policy = await requireKnownVaccineType(dependencies, parsed.vaccineType);
    await verifyAndClaimSealedRecordDocuments(parsed.stableId, AREA, parsed.documents, dependencies);
    const nextDue = resolveNextDue({
        dateAdministered: parsed.dateAdministered,
        vaccineType: parsed.vaccineType,
        nextDue: parsed.nextDue,
        nextDueOverridden: parsed.nextDueOverridden,
        vaccineTypes: policy.vaccineTypes,
    });
    const now = dependencies.clock.now();
    return dependencies.repository.createVaccinationRecord({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        vaccineType: parsed.vaccineType,
        dueDate: parsed.dueDate,
        scheduledNotes: parsed.scheduledNotes,
        dateAdministered: parsed.dateAdministered,
        batchSerial: parsed.batchSerial,
        outcomeNotes: parsed.outcomeNotes,
        nextDue,
        nextDueOverridden: parsed.nextDueOverridden,
        documents: parsed.documents,
        actorId: actor.account.id,
        now,
    });
}
/** `next_due` is never client-computed: when not overridden, it is always
 * derived here from the vaccine type's configured interval, ignoring
 * whatever (if anything) the client sent for `nextDue` in that case —
 * mirrors `vaccination-interval.ts`'s own doc comment that the interval
 * policy is "isolated behind one function". */
function resolveNextDue(input) {
    if (input.dateAdministered === null)
        return null;
    if (input.nextDueOverridden)
        return input.nextDue;
    return computeNextDue({
        dateAdministered: input.dateAdministered,
        vaccineType: input.vaccineType,
        vaccineTypes: input.vaccineTypes,
    });
}
/**
 * Logs a dose against an existing scheduled/placeholder entry, or corrects
 * an already-logged one. `vaccineType` is not resubmitted by the client
 * (`logVaccinationRecordOutcomeSchema`) — it is read off the existing entry
 * so `next_due` can be computed from the correct interval.
 */
export async function logVaccinationRecordOutcome(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = logVaccinationRecordOutcomeSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const existing = await dependencies.repository.getVaccinationRecord(parsed.stableId, parsed.recordId);
    if (existing === null)
        throw stableNotFound();
    const policy = await dependencies.policy.get();
    await verifyAndClaimSealedRecordDocuments(parsed.stableId, AREA, parsed.documents, dependencies);
    const nextDue = resolveNextDue({
        dateAdministered: parsed.dateAdministered,
        vaccineType: existing.vaccineType,
        nextDue: parsed.nextDue,
        nextDueOverridden: parsed.nextDueOverridden,
        vaccineTypes: policy.vaccineTypes,
    });
    const now = dependencies.clock.now();
    const record = await dependencies.repository.logVaccinationRecordOutcome({
        stableId: parsed.stableId,
        recordId: parsed.recordId,
        expectedVersion: parsed.expectedVersion,
        actorId: actor.account.id,
        vaccineType: existing.vaccineType,
        dateAdministered: parsed.dateAdministered,
        batchSerial: parsed.batchSerial,
        outcomeNotes: parsed.outcomeNotes,
        nextDue,
        nextDueOverridden: parsed.nextDueOverridden,
        documents: parsed.documents,
        now,
    });
    if (record === null)
        throw stableConflict();
    return record;
}
export async function listHorseVaccinationRecords(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listHorseVaccinationRecordsSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    return dependencies.repository.listHorseVaccinationRecords(parsed);
}
export function getVaccinationRecordDocumentUrl(principal, input, dependencies) {
    return getSealedRecordDocumentUrl(principal, input, FEATURE, (stableId, documentId) => dependencies.repository.getVaccinationRecordDocument(stableId, documentId), dependencies);
}
